
import React from 'react';
import { NavLink } from 'react-router-dom';
import { UserRole } from '../../types';
import { LogoPrimary } from '../common/Logo';
import { ChevronRight, LayoutGrid, ArrowRightLeft, Landmark, FileBarChart, Users2, ShieldCheck, Database, Zap, Settings } from 'lucide-react';

interface SidebarProps {
  role: UserRole;
}

const Sidebar: React.FC<SidebarProps> = ({ role }) => {
  const userLinks = [
    { label: 'Dashboard', path: '/user/dashboard', icon: <LayoutGrid size={20} /> },
    { label: 'Market Trades', path: '/user/trades', icon: <ArrowRightLeft size={20} /> },
    { label: 'Capital Flows', path: '/user/withdrawals', icon: <Landmark size={20} /> },
    { label: 'Analytical Hub', path: '/user/reports', icon: <FileBarChart size={20} /> },
  ];

  const adminLinks = [
    { label: 'Platform Core', path: '/admin/dashboard', icon: <Database size={20} /> },
    { label: 'Participants', path: '/admin/users', icon: <Users2 size={20} /> },
    { label: 'Compliance', path: '/admin/kyc', icon: <ShieldCheck size={20} /> },
    { label: 'Treasury Out', path: '/admin/withdrawals', icon: <Landmark size={20} /> },
    { label: 'Execution Engine', path: '/admin/trading', icon: <Zap size={20} /> },
    { label: 'Global Reports', path: '/admin/reports', icon: <FileBarChart size={20} /> },
    { label: 'Kernel Config', path: '/admin/settings', icon: <Settings size={20} /> },
  ];

  const links = role === UserRole.ADMIN ? adminLinks : userLinks;

  return (
    <aside className="hidden md:flex flex-col w-72 h-screen sticky top-0 bg-brand-dark border-r border-white/5 px-6 py-10 shrink-0">
      <div className="flex items-center mb-16 px-2">
        <LogoPrimary width={200} height={50} />
      </div>

      <nav className="flex-1 space-y-2">
        {links.map((link) => (
          <NavLink
            key={link.path}
            to={link.path}
            className={({ isActive }) => `
              flex items-center justify-between px-6 py-4 rounded-[1.25rem] transition-all duration-500 group
              ${isActive 
                ? 'bg-brand-primary text-black font-black shadow-[0_10px_20px_-5px_rgba(255,206,27,0.3)]' 
                : 'text-brand-muted hover:text-white hover:bg-white/5'}
            `}
          >
            <div className="flex items-center gap-4">
              <span className={`transition-transform duration-500 group-hover:scale-110`}>{link.icon}</span>
              <span className="text-[10px] uppercase tracking-[0.15em] font-black">{link.label}</span>
            </div>
            <ChevronRight size={14} className={`opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-[-10px] group-hover:translate-x-0`} />
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-8">
        <div className="p-6 bg-brand-primary/5 rounded-[2rem] border border-brand-primary/10 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-16 h-16 bg-brand-primary opacity-5 blur-2xl group-hover:opacity-10 transition-opacity"></div>
          <p className="text-[9px] text-brand-muted uppercase font-black tracking-widest mb-1 opacity-50">Nexus Protocol</p>
          <p className="text-[10px] text-white font-bold tracking-widest uppercase">Encryption Active</p>
          <div className="flex gap-1 mt-2">
            {[1,2,3,4].map(i => <div key={i} className="h-0.5 flex-1 bg-brand-primary/20 rounded-full overflow-hidden"><div className="h-full bg-brand-primary animate-pulse" style={{animationDelay: `${i*0.2}s`}}></div></div>)}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
